package com.heelion.farmafreelance.agent

import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.LocalDate
import kotlinx.datetime.minus
import kotlinx.datetime.plus

/** Un periodo detto in una ricerca ("a novembre", "la prossima settimana", "dal 3 al 10 novembre"), estremi compresi. */
data class PeriodoRicerca(val dal: LocalDate, val al: LocalDate, val descrizione: String) {
    operator fun contains(giorno: LocalDate): Boolean = giorno in dal..al
}

object EstrattorePeriodo {
    private val MESI = listOf(
        "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
        "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"
    )
    private val NOMI_MESI = MESI.joinToString("|")

    // "dal 3 al 10 novembre", "dal 28 ottobre al 4 novembre", "dal 3 fino al 10"
    private val INTERVALLO = Regex("\\bdal (\\d{1,2})(?: ($NOMI_MESI))? (?:al|fino al) (\\d{1,2})(?: ($NOMI_MESI))?\\b")
    // "a novembre", "per il periodo di novembre", "tutto novembre"; non "10 novembre" (quello è un giorno).
    private val MESE = Regex("(?<!\\d )\\b(?:a|ad|di|per|nel|in|tutto|durante|mese di|periodo di)(?: (?:il|tutto il|l|tutto))?(?: (?:mese|periodo) di)? ($NOMI_MESI)\\b")
    private val PROSSIMI_GIORNI = Regex("\\bprossimi (\\d{1,2}) giorni\\b")
    private val PROSSIMA_SETTIMANA = Regex("\\b(la )?(prossima settimana|settimana prossima)\\b")
    private val QUESTA_SETTIMANA = Regex("\\b(questa settimana|in settimana|entro la settimana)\\b")
    private val WEEKEND = Regex("\\b(per il |nel |questo )?(weekend|week end|fine settimana)\\b")
    private val MESE_PROSSIMO = Regex("\\b(per |nel )?(il )?(mese prossimo|prossimo mese)\\b")
    private val QUESTO_MESE = Regex("\\b(per |entro )?(questo mese|entro il mese|entro fine mese)\\b")

    /** [testo] senza il periodo ("Rho a novembre" -> "Rho"), per leggere il luogo senza confonderlo con le date. */
    fun senzaPeriodo(testo: String): String =
        listOf(INTERVALLO, PROSSIMI_GIORNI, PROSSIMA_SETTIMANA, QUESTA_SETTIMANA, WEEKEND, MESE_PROSSIMO, QUESTO_MESE, MESE)
            .fold(testo) { t, re -> Regex(re.pattern, RegexOption.IGNORE_CASE).replace(t, " ") }
            .replace(Regex("\\s+"), " ").trim()

    /** Il periodo detto in [testo], null se non ne nomina (un giorno singolo resta a VoiceCommandParser). */
    fun estrai(testo: String, oggi: LocalDate): PeriodoRicerca? {
        val t = IntentClassifier.normalizza(testo)
        INTERVALLO.find(t)?.let { m -> intervallo(m, oggi)?.let { return it } }
        PROSSIMI_GIORNI.find(t)?.let { m ->
            val n = m.groupValues[1].toInt().coerceIn(1, 90)
            return PeriodoRicerca(oggi, oggi.plus(n - 1, DateTimeUnit.DAY), "nei prossimi $n giorni")
        }
        return when {
            PROSSIMA_SETTIMANA.containsMatchIn(t) -> {
                val lunedi = inizioSettimana(oggi).plus(7, DateTimeUnit.DAY)
                PeriodoRicerca(lunedi, lunedi.plus(6, DateTimeUnit.DAY), "la prossima settimana")
            }
            QUESTA_SETTIMANA.containsMatchIn(t) ->
                PeriodoRicerca(oggi, inizioSettimana(oggi).plus(6, DateTimeUnit.DAY), "questa settimana")
            WEEKEND.containsMatchIn(t) -> {
                val domenica = inizioSettimana(oggi).plus(6, DateTimeUnit.DAY)
                val sabato = domenica.minus(1, DateTimeUnit.DAY)
                PeriodoRicerca(if (oggi > sabato) oggi else sabato, domenica, "nel weekend")
            }
            MESE_PROSSIMO.containsMatchIn(t) -> {
                val primo = LocalDate(oggi.year, oggi.monthNumber, 1).plus(1, DateTimeUnit.MONTH)
                mese(primo.year, primo.monthNumber, oggi)
            }
            QUESTO_MESE.containsMatchIn(t) -> mese(oggi.year, oggi.monthNumber, oggi)
            else -> MESE.find(t)?.let { m ->
                val numero = MESI.indexOf(m.groupValues[1]) + 1
                // Un mese già passato quest'anno è quello dell'anno prossimo.
                mese(if (numero < oggi.monthNumber) oggi.year + 1 else oggi.year, numero, oggi)
            }
        }
    }

    /** Tutto il mese, ma dal giorno di oggi se è il mese in corso. */
    private fun mese(anno: Int, numero: Int, oggi: LocalDate): PeriodoRicerca {
        val primo = LocalDate(anno, numero, 1)
        val ultimo = primo.plus(1, DateTimeUnit.MONTH).minus(1, DateTimeUnit.DAY)
        return PeriodoRicerca(if (oggi > primo) oggi else primo, ultimo, "a ${MESI[numero - 1]}")
    }

    private fun intervallo(m: MatchResult, oggi: LocalDate): PeriodoRicerca? {
        val (g1, mese1, g2, mese2) = m.destructured
        // Il mese che manca si prende dall'altro estremo; se non ce n'è nessuno, il mese in corso.
        val n2 = mese2.ifEmpty { mese1 }.takeIf { it.isNotEmpty() }?.let { MESI.indexOf(it) + 1 } ?: oggi.monthNumber
        val n1 = mese1.takeIf { it.isNotEmpty() }?.let { MESI.indexOf(it) + 1 } ?: n2
        val anno1 = if (n1 < oggi.monthNumber) oggi.year + 1 else oggi.year
        val dal = runCatching { LocalDate(anno1, n1, g1.toInt()) }.getOrNull() ?: return null
        val anno2 = if (n2 < n1) anno1 + 1 else anno1
        val al = runCatching { LocalDate(anno2, n2, g2.toInt()) }.getOrNull() ?: return null
        if (al < dal) return null
        return PeriodoRicerca(dal, al, "dal ${dal.dayOfMonth}/${dal.monthNumber} al ${al.dayOfMonth}/${al.monthNumber}")
    }

    private fun inizioSettimana(d: LocalDate): LocalDate = d.minus(d.dayOfWeek.ordinal - DayOfWeek.MONDAY.ordinal, DateTimeUnit.DAY)
}
